import { useEffect, useState } from 'react'

import { Field, Form, Formik } from 'formik'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Typography from '@material-ui/core/Typography'
import { InputField } from '../../components/InputField'
import { InputSwitch } from '../../components/InputSwitch'
import { SelectCountry } from '../../components/SelectCountry'

import { i18n } from '../../translate/i18n'

import toastError from '../../errors/toastError'
import api from '../../services/api'

import { Grid } from '@material-ui/core'
import { ContactTags } from '../ContactTags'

import { TriangleAlert } from 'lucide-react'
import QueueSelectSingle from '../QueueSelectSingle'
import { useStyles } from './style'
import { QueueDepartmentSelect } from '../QueueDepartmentSelect'

const formatNumberForSystem = number => {
  if (!number) return number
  let cleanNumber = number.replace(/\D/g, '')

  if (cleanNumber.startsWith('55')) {
    if (cleanNumber.length === 13) return cleanNumber.substring(2)
    if (cleanNumber.length === 12) {
      const ddd = cleanNumber.substring(2, 4)
      const phoneNumber = cleanNumber.substring(4)
      return `${ddd}9${phoneNumber}`
    }
    if (cleanNumber.length === 11) return cleanNumber.substring(2)
  }

  if (cleanNumber.length === 10) {
    const ddd = cleanNumber.substring(0, 2)
    const phoneNumber = cleanNumber.substring(2)
    return `${ddd}9${phoneNumber}`
  }

  return cleanNumber
}

const formatSite = site => {
  if (!site) return site
  const trimmedSite = site.trim()

  if (trimmedSite.startsWith('http://') || trimmedSite.startsWith('https://')) {
    if (
      trimmedSite.startsWith('http://www.') ||
      trimmedSite.startsWith('https://www.')
    ) {
      return trimmedSite
    }
    if (trimmedSite.startsWith('http://')) {
      const withoutProtocol = trimmedSite.substring(7)
      if (withoutProtocol.includes('.')) return `http://www.${withoutProtocol}`
    } else if (trimmedSite.startsWith('https://')) {
      const withoutProtocol = trimmedSite.substring(8)
      if (withoutProtocol.includes('.')) return `https://www.${withoutProtocol}`
    }
    return trimmedSite
  }

  if (trimmedSite.includes('.')) {
    if (trimmedSite.startsWith('www.')) return `http://${trimmedSite}`
    return `http://www.${trimmedSite}`
  }

  return trimmedSite
}

const validatePhone = phone => {
  const phoneNumber = phone.replace(/\D/g, '')
  return phoneNumber.length >= 9 && phoneNumber.length <= 11
}

const ContactSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Too Short!')
    .max(50, 'Too Long!')
    .required('Required'),
  // number: Yup.string().min(8, 'Too Short!').max(50, 'Too Long!'),
  email: Yup.string().email('Invalid email')
})

const initialState = {
  name: '',
  number: '',
  email: '',
  site: '',
  pessoa: '',
  disableBot: false,
  blacklist: false,
  silencioso: false,
  defaultQueueId: ''
}

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
  const classes = useStyles()
  const [, setPhone] = useState('')
  const [modalOpenWarning, setModalOpenWarning] = useState(false)
  const [contact, setContact] = useState(initialState)
  const [tags, setTags] = useState(contact.tags || [])

  const isEditing = Boolean(contactId)

  // Função para formatar o site: adicionar apenas http:// se necessário
  const formatSite = site => {
    if (!site) return site

    const trimmedSite = site.trim()

    // Se já tem protocolo (http:// ou https://)
    if (
      trimmedSite.startsWith('http://') ||
      trimmedSite.startsWith('https://')
    ) {
      return trimmedSite
    }

    // Se não tem protocolo
    if (trimmedSite.includes('.')) {
      return `http://${trimmedSite}`
    }
    return trimmedSite
  }

  useEffect(() => {
    const fetchContact = async () => {
      if (initialValues) {
        const formattedInitialValues = {
          ...initialValues,
          number: initialValues.number
            ? formatNumberForSystem(initialValues.number)
            : initialValues.number
        }

        setContact(prevState => {
          return { ...prevState, ...formattedInitialValues }
        })
      }

      if (!isEditing) return

      try {
        const { data } = await api.get(`/contacts/${contactId}`)
        setContact(data)
      } catch (err) {
        toastError(err)
      }
    }

    fetchContact()
  }, [contactId, open, initialValues, isEditing])

  useEffect(() => {
    setTags(contact?.tags)
  }, [contact])

  const handleClose = () => {
    onClose()
    setContact(initialState)
  }

  const handleSaveContact = async values => {
    // 1. Limpa o número mantendo o formato digitado (com ou sem o 9)
    let formattedNumber = formatNumberForSystem(values.number || '')

    // 2. Garante o DDI (55) na string caso o usuário não tenha selecionado/digitado
    if (!formattedNumber.startsWith('55') && formattedNumber.length <= 11) {
      formattedNumber = `55${formattedNumber}`
    }

    try {
      // 3. Monta o payload definitivo com o número íntegro para o backend tratar
      const formattedValues = {
        ...values,
        number: formattedNumber,
        site: formatSite(values.site),
        defaultQueueId:
          values.defaultQueueId === '' ? null : values.defaultQueueId
      }

      if (isEditing) {
        await api.put(`/contacts/${contactId}`, formattedValues)
        handleClose()
      } else {
        const { data } = await api.post('/contacts', formattedValues)
        if (onSave) {
          onSave(data)
        }
        handleClose()
      }
      toast.success(i18n.t('contactModal.success'))
    } catch (err) {
      toastError(err)
    }
  }

  const handleRemoveTag = tagId => {
    api
      .delete(`/contacts/${contact.id}/tags/${tagId}`)
      .then(() => {
        const newTags = tags.filter(tag => tag.id !== tagId)
        setTags(newTags)
      })
      .catch(err => {
        console.error('Failed to remove tag from contact', err)
      })
  }

  const handleAddTag = tag => {
    api
      .post(`/contacts/${contact.id}/tags`, { tagId: tag.id })
      .then(() => {
        setTags([...tags, tag])
      })
      .catch(err => {
        console.error('Failed to add tag to contact', err)
      })
  }

  const validatePhone = phone => {
    const phoneNumber = phone.replace(/\D/g, '')
    return phoneNumber.length >= 9 && phoneNumber.length <= 11
  }

  const warningModal = () => {
    return (
      <Dialog
        open={modalOpenWarning}
        onClose={() => setModalOpenWarning(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialogWarning}
      >
        <DialogTitle
          id="alert-dialog-title"
          className={classes.dialogWarningTitle}
        >
          <div className={classes.warningContainer}>
            <TriangleAlert size={34} />
            <span className={classes.warningTitle}>Atenção!</span>
          </div>
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          <span className={classes.warningDescription}>
            O número de telefone deve conter entre 9 e 11 dígitos, incluindo o
            código de área (DDD), e deve ser informado sem espaços, traços ou
            parênteses, seguindo o formato: 67912345678.
          </span>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setModalOpenWarning(false)} color="primary">
            Entendi
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} scroll="paper">
        <DialogTitle id="form-dialog-title" className={classes.modalTitle}>
          {contactId
            ? `${i18n.t('contactModal.title.edit')}`
            : `${i18n.t('contactModal.title.add')}`}
        </DialogTitle>
        <Formik
          initialValues={contact}
          enableReinitialize={true}
          validationSchema={ContactSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveContact(values)
              actions.setSubmitting(false)
            }, 400)
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  className={classes.modalFormTitle}
                >
                  {i18n.t('contactModal.form.mainInfo')}
                </Typography>

                {/* Criado o componente InputField com o formik */}
                <Grid container spacing={1} columnSpacing={2}>
                  <Grid item xs={6}>
                    <InputField
                      name="name"
                      label={i18n.t('contactModal.form.name')}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Field name="number">
                      {({ field, form }) => (
                        <SelectCountry
                          {...field}
                          label="País / Telefone"
                          value={field.value || ''}
                          onChange={val => {
                            form.setFieldValue('number', val)
                            setPhone(val)
                          }}
                          disabled={isEditing}
                          isEditing={isEditing}
                        />
                      )}
                    </Field>
                  </Grid>

                  <Grid item xs={6}>
                    <InputField
                      name="email"
                      label="E-mail"
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                      placeholder="E-mail"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <InputField
                      name="site"
                      label="Site"
                      error={touched.site && Boolean(errors.site)}
                      helperText={touched.site && errors.site}
                      placeholder="Site"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <InputField
                      name="apelido"
                      label="Apelido"
                      error={touched.apelido && Boolean(errors.apelido)}
                      helperText={touched.apelido && errors.apelido}
                      placeholder="Apelido"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <InputField
                      name="empresa"
                      label="Empresa"
                      placeholder="Nome da Empresa"
                    />
                  </Grid>

                  <Grid item xs={12} md={12} sm={12}>
                    <Field name="defaultQueueId">
                      {({ field, form }) => (
                        <QueueDepartmentSelect
                          label={i18n.t('contactModal.form.defaultQueue')}
                          placeholder="Selecione o departamento padrão"
                          fetchAllQueues={true}
                          showLeafQueuesOnly={true}
                          includeAllOption={false}
                          showBots={false}
                          isMulti={false}
                          allowEmpty={true}
                          disabled={isSubmitting}
                          value={field.value ? [{ id: field.value }] : []}
                          onChange={selectedItems => {
                            const selectedObj =
                              selectedItems.length > 0 ? selectedItems[0] : null
                            form.setFieldValue(
                              'defaultQueueId',
                              selectedObj ? selectedObj.id : ''
                            )
                          }}
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} md={4} sm={4}>
                    <Field name="pessoa">
                      {({ field, form }) => (
                        <InputSwitch
                          label=" Empresa"
                          size="small"
                          checked={field.value === 'Juridica'}
                          onChange={() =>
                            form.setFieldValue(
                              'pessoa',
                              field.value === 'Juridica' ? 'Fisica' : 'Juridica'
                            )
                          }
                          name="pessoa"
                          color="primary"
                        />
                      )}
                    </Field>
                  </Grid>

                  <Grid item xs={12} md={4} sm={4}>
                    <Field name="blacklist">
                      {({ field, form }) => (
                        <InputSwitch
                          label="Bloquear Contato"
                          size="small"
                          checked={field.value}
                          onChange={() =>
                            form.setFieldValue('blacklist', !field.value)
                          }
                          name="blacklist"
                          color="primary"
                        />
                      )}
                    </Field>
                  </Grid>

                  <Grid item xs={12} md={4} sm={4}>
                    <Field name="disableBot">
                      {({ field, form }) => (
                        <InputSwitch
                          label={'Bot'}
                          size="small"
                          checked={field.value}
                          onChange={() =>
                            form.setFieldValue('disableBot', !field.value)
                          }
                          name="disableBot"
                          color="primary"
                        />
                      )}
                    </Field>
                  </Grid>

                  <Grid item xs={12} md={5} sm={5}>
                    <Field name="silencioso">
                      {({ field, form }) => (
                        <InputSwitch
                          label={'Finalização Silenciosa'}
                          size="small"
                          checked={field.value}
                          onChange={() =>
                            form.setFieldValue('silencioso', !field.value)
                          }
                          name="silencioso"
                          color="primary"
                        />
                      )}
                    </Field>
                  </Grid>

                  {contactId && (
                    <Grid item xs={12} md={12} sm={12}>
                      <Typography
                        variant="subtitle1"
                        className={classes.titleExtraInfo}
                      >
                        Tags do contato
                      </Typography>
                      <ContactTags
                        contact={contact}
                        tags={tags}
                        handleRemoveTag={handleRemoveTag}
                        handleAddTag={handleAddTag}
                        filterTags={['contato']} // adicionado para exibir apenas as tags de contato
                      />
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                >
                  {i18n.t('contactModal.buttons.cancel')}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.buttonSave}
                >
                  {contactId
                    ? `${i18n.t('contactModal.buttons.okEdit')}`
                    : `${i18n.t('contactModal.buttons.okAdd')}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
      {warningModal()}
    </div>
  )
}

export default ContactModal
